import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IUserClaims } from '@mynexus/mynexus-ui-lib';
import { Observable, catchError, map, of } from 'rxjs';
import { API_URL, PLATFORM_API_URL } from '../constants';
import { UserClinicalCredentials, UserGroup } from '../model';
import { IAuthenticationService, ShellService } from '@mynexus/mfe-core';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService implements IAuthenticationService {
  constructor(
    private sharedServices: ShellService,
    private http: HttpClient,
    @Inject(API_URL) private apiUrl: string,
    @Inject(PLATFORM_API_URL) private platformApiUrl: string,
  ) {
  }

  getToken(): Promise<string> {
    return this.sharedServices.authenticationService.getToken();
  }

  get userClaims(): IUserClaims | null {
    return this.sharedServices.authenticationService.userClaims;
  }

  get userEmail(): string | null {
    return this.sharedServices.authenticationService.userEmail;
  }

  get userGroups(): string[] {
    return this.sharedServices.authenticationService.userGroups;
  }

  get userName(): string {
    return this.sharedServices.authenticationService.userName;
  }

  get userPermissions(): string[] {
    return this.sharedServices.authenticationService.userPermissions;
  }

  get isExternalUser(): boolean {
    return !!this.userClaims?.extension_isExternal;
  }

  get hasHealthPlanCaseManager(): boolean {
    return this.hasGroup('HealthPlanCaseManagerExternal');
  }

  get isDMEPOSMedicareOperations(): boolean {
    return this.hasGroup(UserGroup.DMEPOSMedicareOperations);
  }

  get isDMEPOSMedicareOperationsAdmin(): boolean {
    return this.hasGroup(UserGroup.DMEPOSMedicareOperationsAdmin);
  }

  get isDMEPOSMedicaidOperations(): boolean {
    return this.hasGroup(UserGroup.DMEPOSMedicaidOperations);
  }

  get isDMEPOSMedicaidOperationsAdmin(): boolean {
    return this.hasGroup(UserGroup.DMEPOSMedicaidOperationsAdmin);
  }

  get isDMEPOSProviderExternal(): boolean {
    return this.hasGroup(UserGroup.DMEPOSProviderExternal);
  }

  get isHealthPlanCaseManager(): boolean {
    return this.hasGroup(UserGroup.HealthPlanCaseManagerExternal);
  }

  get isDMEPOSOperations(): boolean {
    return this.isDMEPOSMedicareOperations || this.isDMEPOSMedicaidOperations;
  }

  get isDMEPOSOperationsAdmin(): boolean {
    return this.isDMEPOSMedicareOperationsAdmin || this.isDMEPOSMedicaidOperationsAdmin;
  }

  getUserProfileCredentialsByEmails(emails: string[]): Observable<UserClinicalCredentials[]> {
    return this.http.post<UserClinicalCredentials[]>(`${this.platformApiUrl}/api/Graph/GetUserProfilesListCredentialsByEmails`, emails);
  }

  getUserCredentialsByEmail(email: string): Observable<UserClinicalCredentials> {
    return this.getUserProfileCredentialsByEmails([email]).pipe(
      catchError(() => of(email)),
      map((data: UserClinicalCredentials[]) => {
        return data[0]
      }),
    )
  }

  hasGroup(groupName: keyof typeof UserGroup): boolean {
    if (this.userClaims?.groups) {
      return this.userClaims.groups.includes(groupName);
    }

    return false;
  }
}
